<template>
  <section
    class="container digital-reference-page"
    :data-view-ready="!loading"
    :data-cy="`digital-reference-${resolvedType}-page`">
    <div
      v-if="loading"
      class="text-center mt-5">
      <coozzy-spinner />
    </div>
    <template v-else-if="submitted">
      <div
        class="digital-reference-success"
        data-cy="digital-reference-success">
        <p class="mb-1">
          {{ $t('message.digitalReference.successTitle') }}
        </p>
        <p class="mb-0">
          {{ $t('message.digitalReference.successSubtitle') }}
        </p>
      </div>
    </template>
    <template v-else-if="!form">
      <div class="row mt-4">
        <div class="col">
          <p
            class="mb-0"
            data-cy="digital-reference-invalid-link">
            {{ $t('message.digitalReference.invalidLink') }}
          </p>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col">
          <h1 class="digital-reference-title mt-4">
            <span class="digital-reference-title-line">{{ pageTitlePrefix }}</span>
            <span class="digital-reference-title-name">{{ applicantName }}</span>
          </h1>
          <p data-cy="digital-reference-description">
            {{ pageDescription }}
          </p>
        </div>
      </div>
      <digital-reference-info-cards
        :applicant="form.applicant"
        :administration="form.administration" />
      <div class="row mb-3">
        <div class="col-12">
          <digital-reference-questions-card
            :questions="questions"
            :show-errors="showErrors"
            :reference-type="resolvedType"
            @change="onAnswerChange" />
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-12 col-lg-6">
          <coozzy-form-input
            v-model="contactPerson"
            data-cy="digital-reference-contact-person"
            :name="$t('message.digitalReference.contactPerson') + '*'"
            :state="showErrors && !contactPerson.trim() ? false : null" />
        </div>
      </div>
      <div class="row align-items-end mb-4">
        <div class="col-12 col-lg-8 mb-3 mb-lg-0">
          <label for="digital-reference-notes">{{ $t('message.digitalReference.notes') }}</label>
          <coozzy-form-textarea
            id="digital-reference-notes"
            v-model="notes"
            data-cy="digital-reference-notes"
            :text="notes"
            :initial="notes"
            :show-label="false"
            :rows="3" />
        </div>
        <div class="col-12 col-lg-4 text-lg-right">
          <coozzy-button
            design="primary"
            class="px-5"
            data-cy="digital-reference-send-button"
            :disabled="submitting"
            @click="submit">
            {{ $t('message.generic.form.send') }}
          </coozzy-button>
        </div>
      </div>
      <div
        v-if="form.administration && form.administration.telephone"
        class="row mb-4">
        <div class="col-12">
          <span class="contact-line">{{ $t('message.digitalReference.anyQuestion', {
            telephone: form.administration.telephone
          }) }}</span>
        </div>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import DigitalReferenceInfoCards from '@/marketing/components/DigitalReferenceInfoCards.vue'
import DigitalReferenceQuestionsCard from '@/marketing/components/DigitalReferenceQuestionsCard.vue'
import DigitalReferenceApi from '@/misc/apis/DigitalReferenceApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import UserApi from '@/misc/apis/UserApi'
import {
  formatEmploymentDuration,
  formatEmploymentType,
  formatIncome,
  formatPersonName,
  formatPreviousGrossRent,
  formatRentedSince,
  getReferenceFormAccountId,
  getReferenceFormLogoUrl,
  getReferenceFormObjectId,
  localeForApiLanguage
} from '@/misc/utils/digitalReferenceFormatters'
import {
  V2ReferenceConfirmation,
  V2ReferenceType,
  V2ReferenceYesNo,
  V2SubmitEmployerReferenceAnswers,
  V2SubmitLandlordReferenceAnswers
} from '@/types/api'
import {
  DigitalReferenceChoice,
  DigitalReferenceChoiceValue,
  DigitalReferenceFormResponse,
  DigitalReferenceQuestion,
  DigitalReferenceType
} from '@/types/models'

// Statuses the backend uses for a link that was already submitted or finalized manually.
const INVALID_LINK_STATUS = [400, 403, 404, 409, 410]

function emptyEmployerAnswers(): V2SubmitEmployerReferenceAnswers {
  return {
    duration: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED,
    probationOngoing: V2ReferenceYesNo.REFERENCE_YES_NO_UNDEFINED,
    temporaryPosition: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED,
    stillInEffect: V2ReferenceYesNo.REFERENCE_YES_NO_UNDEFINED,
    salaryCorrect: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED
  }
}

function emptyLandlordAnswers(): V2SubmitLandlordReferenceAnswers {
  return {
    duration: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED,
    rentPaidOnTime: V2ReferenceYesNo.REFERENCE_YES_NO_UNDEFINED,
    complaintsOrConflicts: V2ReferenceYesNo.REFERENCE_YES_NO_UNDEFINED,
    monthlyRent: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED
  }
}

function isInvalidLinkError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status
  return status !== undefined && INVALID_LINK_STATUS.includes(status)
}

export default Vue.extend({
  name: 'DigitalReferenceFormPage',
  components: {
    CoozzySpinner,
    CoozzyFormInput,
    CoozzyFormTextarea,
    CoozzyButton,
    DigitalReferenceInfoCards,
    DigitalReferenceQuestionsCard
  },
  metaInfo() {
    return {
      title: this.pageTitle || this.$t('message.digitalReference.invalidLink')
    }
  },
  props: {
    publicId: {
      type: String,
      required: true
    },
    referenceType: {
      type: String as PropType<DigitalReferenceType>,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      submitting: false,
      submitted: false,
      showErrors: false,
      form: null as DigitalReferenceFormResponse | null,
      headerLogoBackup: null as string | null,
      replacedHeaderLogo: false,
      isPageDestroyed: false,
      contactPerson: '',
      notes: '',
      employerAnswers: emptyEmployerAnswers(),
      landlordAnswers: emptyLandlordAnswers()
    }
  },
  computed: {
    // The URL carries the type for readability, but the response is authoritative.
    resolvedType(): DigitalReferenceType {
      if (this.form?.type === V2ReferenceType.REFERENCE_TYPE_EMPLOYER) {
        return 'employer'
      }
      if (this.form?.type === V2ReferenceType.REFERENCE_TYPE_LANDLORD) {
        return 'landlord'
      }
      return this.referenceType
    },
    isEmployer(): boolean {
      return this.resolvedType === 'employer'
    },
    applicantName(): string {
      return formatPersonName(this.form?.applicant?.name)
    },
    pageTitlePrefix(): string {
      if (!this.form) {
        return ''
      }
      const key = this.isEmployer
        ? 'message.digitalReference.employer.titlePrefix'
        : 'message.digitalReference.landlord.titlePrefix'
      return this.$t(key) as string
    },
    pageTitle(): string {
      if (!this.form) {
        return ''
      }
      return [this.pageTitlePrefix, this.applicantName].filter(Boolean).join(' ')
    },
    pageDescription(): string {
      const key = this.isEmployer
        ? 'message.digitalReference.employer.description'
        : 'message.digitalReference.landlord.description'
      return this.$t(key, { name: this.applicantName }) as string
    },
    appliesChoices(): DigitalReferenceChoice[] {
      return [
        { value: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_APPLIES, label: this.$t('message.digitalReference.choices.applies') as string },
        { value: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_DOES_NOT_APPLY, label: this.$t('message.digitalReference.choices.doesNotApply') as string },
        { value: V2ReferenceConfirmation.REFERENCE_CONFIRMATION_NO_INFORMATION, label: this.$t('message.digitalReference.choices.noInformation') as string }
      ]
    },
    yesNoChoices(): DigitalReferenceChoice[] {
      return [
        { value: V2ReferenceYesNo.REFERENCE_YES_NO_YES, label: this.$t('message.digitalReference.choices.yes') as string },
        { value: V2ReferenceYesNo.REFERENCE_YES_NO_NO, label: this.$t('message.digitalReference.choices.no') as string },
        { value: V2ReferenceYesNo.REFERENCE_YES_NO_NO_INFORMATION, label: this.$t('message.digitalReference.choices.noInformation') as string }
      ]
    },
    questions(): DigitalReferenceQuestion[] {
      if (!this.form) {
        return []
      }
      if (this.isEmployer) {
        const prefill = this.form.employerPrefill
        return [
          {
            key: 'duration',
            label: this.$t('message.digitalReference.employer.questions.employmentDuration') as string,
            applicantValue: formatEmploymentDuration(prefill?.employmentDuration),
            value: this.employerAnswers.duration,
            choices: this.appliesChoices
          },
          {
            key: 'probationOngoing',
            label: this.$t('message.digitalReference.employer.questions.probationOngoing') as string,
            applicantValue: '',
            value: this.employerAnswers.probationOngoing,
            choices: this.yesNoChoices
          },
          {
            key: 'temporaryPosition',
            label: this.$t('message.digitalReference.employer.questions.temporaryPosition') as string,
            applicantValue: formatEmploymentType(prefill?.employmentType),
            value: this.employerAnswers.temporaryPosition,
            choices: this.appliesChoices
          },
          {
            key: 'stillInEffect',
            label: this.$t('message.digitalReference.employer.questions.employmentInEffect') as string,
            applicantValue: '',
            value: this.employerAnswers.stillInEffect,
            choices: this.yesNoChoices
          },
          {
            key: 'salaryCorrect',
            label: this.$t('message.digitalReference.employer.questions.wageDetailsCorrect') as string,
            applicantValue: formatIncome(prefill?.income),
            value: this.employerAnswers.salaryCorrect,
            choices: this.appliesChoices
          }
        ]
      }
      const prefill = this.form.landlordPrefill
      return [
        {
          key: 'duration',
          label: this.$t('message.digitalReference.landlord.questions.tenancyDuration') as string,
          applicantValue: formatRentedSince(prefill?.rentedSince),
          value: this.landlordAnswers.duration,
          choices: this.appliesChoices
        },
        {
          key: 'rentPaidOnTime',
          label: this.$t('message.digitalReference.landlord.questions.rentPaidOnTime') as string,
          applicantValue: '',
          value: this.landlordAnswers.rentPaidOnTime,
          choices: this.yesNoChoices
        },
        {
          key: 'complaintsOrConflicts',
          label: this.$t('message.digitalReference.landlord.questions.complaintsOrConflicts') as string,
          applicantValue: '',
          value: this.landlordAnswers.complaintsOrConflicts,
          choices: this.yesNoChoices
        },
        {
          key: 'monthlyRent',
          label: this.$t('message.digitalReference.landlord.questions.monthlyRent') as string,
          applicantValue: formatPreviousGrossRent(prefill?.previousGrossRent),
          value: this.landlordAnswers.monthlyRent,
          choices: this.appliesChoices
        }
      ]
    },
    isFormValid(): boolean {
      const allAnswered = this.questions.every(question =>
        question.value !== V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED &&
        question.value !== V2ReferenceYesNo.REFERENCE_YES_NO_UNDEFINED)
      return allAnswered && this.contactPerson.trim() !== ''
    }
  },
  created() {
    this.headerLogoBackup = this.$store.getters['user/getLogoUrl'] || null
    this.loadForm()
  },
  beforeDestroy() {
    this.isPageDestroyed = true
    if (this.replacedHeaderLogo) {
      this.$store.dispatch('user/setLogoUrl', this.headerLogoBackup)
    }
  },
  methods: {
    async loadForm() {
      this.loading = true
      try {
        const response = await DigitalReferenceApi.getReferenceForm(this.publicId)
        this.form = response.valid ? response : null
        this.applyLanguage(response)
        await this.applyAccountLogo(response)
      } catch {
        this.form = null
      } finally {
        this.loading = false
      }
    },
    async applyAccountLogo(response: DigitalReferenceFormResponse) {
      let logoUrl = getReferenceFormLogoUrl(response)
      let accountId = getReferenceFormAccountId(response)
      const objectId = getReferenceFormObjectId(response)
      if (!logoUrl && !accountId && objectId) {
        try {
          const marketingObject = await ObjectApi.getMarketingObject(objectId)
          const marketingAccountId = marketingObject?.marketingAccountId
          if (marketingAccountId && String(marketingAccountId) !== '-1') {
            accountId = String(marketingAccountId)
          }
        } catch (error) {
          console.log(error)
        }
      }
      if (!logoUrl && accountId) {
        try {
          const accountResponse = await UserApi.getAccount(accountId)
          logoUrl = accountResponse?.account?.logoUrl || ''
        } catch (error) {
          console.log(error)
        }
      }
      if (this.isPageDestroyed || !logoUrl) {
        return
      }
      this.replacedHeaderLogo = true
      this.$store.dispatch('user/setLogoUrl', logoUrl)
    },
    applyLanguage(response: DigitalReferenceFormResponse) {
      const locale = localeForApiLanguage(response.language)
      if (locale && locale !== this.$store.getters['internationalization/getCurrentLanguage']) {
        this.$store.dispatch('internationalization/changeCurrentLanguage', locale)
      }
    },
    onAnswerChange(payload: { key: string; value: DigitalReferenceChoiceValue }) {
      const answers = (this.isEmployer ? this.employerAnswers : this.landlordAnswers) as Record<string, DigitalReferenceChoiceValue>
      if (payload.key in answers) {
        answers[payload.key] = payload.value
      }
    },
    scrollSuccessIntoView() {
      const scroller = document.querySelector('.app.application_form')
      if (scroller) {
        scroller.scrollTop = 0
      }
      window.scrollTo(0, 0)
      const success = this.$el.querySelector('[data-cy="digital-reference-success"]')
      if (success && typeof success.scrollIntoView === 'function') {
        success.scrollIntoView({ block: 'start' })
      }
    },
    async submit() {
      this.showErrors = true
      if (!this.isFormValid || !this.form) {
        Vue.toasted.show(this.$t('message.digitalReference.validationError'), { type: 'error' })
        return
      }
      this.submitting = true
      try {
        await DigitalReferenceApi.submitReferenceForm(this.publicId, {
          contactPersonForQueries: this.contactPerson.trim(),
          notes: this.notes,
          employerAnswers: this.isEmployer ? this.employerAnswers : undefined,
          landlordAnswers: this.isEmployer ? undefined : this.landlordAnswers
        })
        this.submitted = true
        this.$nextTick(() => {
          this.scrollSuccessIntoView()
        })
      } catch (error) {
        if (isInvalidLinkError(error)) {
          this.form = null
        } else {
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        }
      } finally {
        this.submitting = false
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.digital-reference-page {
  padding-bottom: 40px;
}

.digital-reference-title {
  color: #333;
  font-size: 1.75rem;
  line-height: 1.3;
}

.digital-reference-title-line,
.digital-reference-title-name {
  display: block;
}

.digital-reference-success {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 120px);
  margin-top: 0;
  padding: calc(1.5rem + env(safe-area-inset-top, 0px)) 1rem 2rem;
  text-align: center;
  font-size: 1.25rem;
  line-height: 1.5;
}

.contact-line {
  line-height: 28px;
}

@media (max-width: 767px) {
  .digital-reference-title {
    font-size: 1.2rem;
    line-height: 1.35;
  }

  .digital-reference-success {
    min-height: calc(100vh - 90px);
    font-size: 1.1rem;
  }
}
</style>
